import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import { BsCloudUpload } from "react-icons/bs";
import { FiTrash2, FiPlus } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";

import ActionButton from "../../Common/ActionButton";
import { InputField, SelectField, TextAreaField } from "../../Common/FormComponents";
import { getallCategory } from "../../../Redux/actions/Operationsactions/getcategory";
import { getallUOM } from "../../../Redux/actions/Operationsactions/getuom";
import { AddMaterialApi, EditMaterialApi } from "../../../Api/Store/Material";
import "./AddStock.css";

const MATERIAL_TYPES = [
  "Consumable / Raw Material",
  "Reusable Tool / Asset",
  "Service / Labor Item",
];

const AddStock = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const categoryData = useSelector((state) => state.getAllCategory?.data || []);
  const uomData = useSelector((state) => state.getAllUOM?.data || []);

  useEffect(() => {
    dispatch(getallCategory());
    dispatch(getallUOM());
  }, [dispatch]);

  const populateData = location.state || {};
  const isEdit = Boolean(populateData?.isEdit || populateData?.Id || populateData?.id);

  const [isLoading, setIsLoading] = useState(false);

  const initialPackagings = (populateData.Packagings || populateData.packagings || []).map((p) => ({
    id: p.Id || p.id,
    packagingName: p.PackagingName || p.packagingName || "",
    uomId: p.UomId || p.uomId || "",
    conversionToBase: p.ConversionToBase ?? p.conversionToBase ?? "",
  }));

  const [packagings, setPackagings] = useState(initialPackagings);

  const [formData, setFormData] = useState({
    // Old fields
    Category: populateData.CategoryId || populateData.Category || "",
    Name: populateData.ItemName || populateData.Name || "",
    ItemCode: populateData.ItemCode || "",
    UOMPurchase: populateData.UomId || populateData.UOMPurchase || "",
    RestockLevel: populateData.RestockLevel ?? 10,
    CostingMethod: populateData.CostingMethod || "FIFO",
    Note: populateData.ItemDescription || populateData.Note || "",

    // New fields
    MaterialType:
      populateData.MaterialType || populateData.materialType || "Consumable / Raw Material",
    SubCategoryId: populateData.SubCategoryId || "",
    Brand: populateData.Brand || populateData.brand || "",
    EstimatedRate: populateData.EstimatedRate ?? populateData.estimatedRate ?? "",
    AllowedWastage: populateData.AllowedWastage ?? populateData.allowedWastage ?? "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // A sub-category belongs to one category, so switching category clears it.
  const handleCategoryChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, Category: value, SubCategoryId: "" }));
  };

  const handleAddPackaging = () => {
    setPackagings((prev) => [
      ...prev,
      {
        packagingName: "",
        uomId: formData.UOMPurchase || "",
        conversionToBase: "",
      },
    ]);
  };

  const handleRemovePackaging = (index) => {
    setPackagings((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePackagingChange = (index, field, value) => {
    setPackagings((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const topLevelCategories = useMemo(
    () => categoryData.filter((c) => !c.ParentCategoryId),
    [categoryData],
  );

  const subCategories = useMemo(
    () =>
      categoryData.filter((c) => c.ParentCategoryId && c.ParentCategoryId === formData.Category),
    [categoryData, formData.Category],
  );

  const uomOptions = useMemo(() => {
    return [
      { value: "", label: "Select Unit" },
      ...uomData.map((u) => ({
        value: u.Id,
        label: u.UomName || u.UOMName,
      })),
    ];
  }, [uomData]);

  const baseUomLabel = useMemo(() => {
    const found = uomOptions.find((u) => String(u.value) === String(formData.UOMPurchase));
    return found?.label && found.value !== "" ? found.label : "Base Unit";
  }, [uomOptions, formData.UOMPurchase]);

  // Delivery helper note calculation
  const getDeliveryNote = (pkgName, conversion) => {
    const name = pkgName?.trim() || "unit";
    const num = parseFloat(conversion);
    if (!num || num <= 0) {
      return null;
    }

    if (num < 1) {
      const count = Math.round((1 / num) * 100) / 100;
      return `When a vendor delivers ${count} ${name}${count !== 1 ? "s" : ""} on site, the store will automatically receive 1 ${baseUomLabel}.`;
    }
    return `When a vendor delivers 1 ${name} on site, the store will automatically receive ${num} ${baseUomLabel}.`;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validations & checks
    if (!formData.Name.trim()) {
      toast.error("Item Name is required");
      return;
    }
    if (!formData.MaterialType) {
      toast.error("Material Type is required");
      return;
    }
    if (!formData.Category.trim()) {
      toast.error("Category is required");
      return;
    }
    if (!formData.UOMPurchase) {
      toast.error("Base Unit (UOM) is required");
      return;
    }

    if (formData.RestockLevel !== "" && Number(formData.RestockLevel) < 0) {
      toast.error("Low Stock / Reorder Alert Level cannot be negative");
      return;
    }

    if (formData.EstimatedRate !== "" && Number(formData.EstimatedRate) < 0) {
      toast.error("Estimated Rate cannot be negative");
      return;
    }

    if (formData.AllowedWastage !== "") {
      const wastage = Number(formData.AllowedWastage);
      if (isNaN(wastage) || wastage < 0 || wastage > 100) {
        toast.error("Allowed Wastage must be a percentage between 0 and 100");
        return;
      }
    }

    // Packaging unit checks
    for (let i = 0; i < packagings.length; i++) {
      const pkg = packagings[i];
      if (!pkg.packagingName.trim()) {
        toast.error(`Packaging unit #${i + 1} requires a Packaging Name`);
        return;
      }
      const conv = parseFloat(pkg.conversionToBase);
      if (isNaN(conv) || conv <= 0) {
        toast.error(`Packaging "${pkg.packagingName}" requires a conversion rate greater than 0`);
        return;
      }
    }

    try {
      setIsLoading(true);

      const payload = {
        // Old fields preserved with exact names
        categoryId: formData.Category,
        itemName: formData.Name,
        uomId: formData.UOMPurchase,
        restockLevel: Number(formData.RestockLevel) || 0,
        itemDescription: formData.Note,
        costingMethod: formData.CostingMethod || "FIFO",
        itemCode: formData.ItemCode.trim() || null,

        // New fields
        materialType: formData.MaterialType,
        subCategoryId: formData.SubCategoryId || null,
        brand: formData.Brand.trim() || null,
        estimatedRate: formData.EstimatedRate !== "" ? Number(formData.EstimatedRate) : null,
        allowedWastage: formData.AllowedWastage !== "" ? Number(formData.AllowedWastage) : null,
      };

      payload.packagings = packagings.map((pkg) => {
        const item = {
          packagingName: pkg.packagingName.trim(),
          uomId: pkg.uomId || formData.UOMPurchase,
          conversionToBase: Number(pkg.conversionToBase),
        };
        if (pkg.id) {
          item.id = pkg.id;
        }
        return item;
      });

      if (isEdit) {
        payload.id = populateData.Id || populateData.id;
        const res = await EditMaterialApi(populateData.Id || populateData.id, payload);
        if (res?.data?.IsSuccess) {
          toast.success("Material updated successfully");
          history.push("/store/material");
        } else {
          toast.error(res?.data?.Message || "Failed to update material");
        }
      } else {
        const res = await AddMaterialApi(payload);
        if (res?.data?.IsSuccess) {
          toast.success("Material added successfully");
          history.push("/store/material");
        } else {
          toast.error(res?.data?.Message || "Failed to add material");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.Message || "Something went wrong while saving material");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="add-stock-wrapper">
      {/* Header bar */}
      <div className="add-stock-header-card">
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="add-stock-back-btn"
            title="Go Back"
            onClick={() => history.goBack()}
          >
            <FaArrowLeft size={14} />
          </button>
          <div>
            <h4 className="add-stock-page-title">{isEdit ? "Edit" : "Add"} Material</h4>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} autoComplete="off">
        {/* Top 2 Segments Side-by-Side */}
        <div className="add-stock-grid-2">
          {/* Segment 1: Basic Information */}
          <div className="add-stock-card">
            <h5 className="add-stock-card-title">Basic Information</h5>

            {/* Item Name */}
            <div className="row mb-2 align-items-center">
              <InputField
                label="Item Name:"
                name="Name"
                value={formData.Name}
                onChange={handleChange}
                placeholder='Deformed Rebar (1/2" / #4)'
                required
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
              <InputField
                label="Item Code / SKU:"
                name="ItemCode"
                value={formData.ItemCode}
                onChange={handleChange}
                placeholder="e.g. MAT-00123"
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
              <SelectField
                label="Material Type:"
                name="MaterialType"
                value={formData.MaterialType}
                onChange={handleChange}
                options={MATERIAL_TYPES.map((type) => ({ value: type, label: type }))}
                required
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
              <SelectField
                label="Category:"
                name="Category"
                value={formData.Category}
                onChange={handleCategoryChange}
                options={[
                  { value: "", label: "Select Category" },
                  ...topLevelCategories.map((cat) => ({
                    value: cat.Id,
                    label: cat.CategoryName,
                  })),
                ]}
                required
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
              <SelectField
                label="Sub-Category:"
                name="SubCategoryId"
                value={formData.SubCategoryId}
                onChange={handleChange}
                disabled={!formData.Category}
                options={[
                  { value: "", label: "Select Sub-Category" },
                  ...subCategories.map((sub) => ({
                    value: sub.Id,
                    label: sub.CategoryName,
                  })),
                ]}
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
              <InputField
                label="Brand / Specification:"
                name="Brand"
                value={formData.Brand}
                onChange={handleChange}
                placeholder="Mughal Steel / Amreli Steel / Grade 60"
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
              <TextAreaField
                label="Note / Description:"
                name="Note"
                value={formData.Note}
                onChange={handleChange}
                rows={2}
                placeholder="Additional technical details, storage conditions, or vendor instructions..."
                stacked
                wrapperCol=" col-12"
              />
            </div>
          </div>

          {/* Segment 2: Units & Stock Alert */}
          <div className="add-stock-card">
            <h5 className="add-stock-card-title">Units & Stock Alert</h5>

            {/* Base Unit (UOM) */}
            <div className="row mb-2 align-items-center">
              <SelectField
                label="Base Unit (UOM):"
                name="UOMPurchase"
                value={formData.UOMPurchase}
                onChange={handleChange}
                options={uomOptions}
                required
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
              <SelectField
                label="Costing Method:"
                name="CostingMethod"
                value={formData.CostingMethod}
                onChange={handleChange}
                options={[
                  { value: "FIFO", label: "FIFO (First In, First Out)" },
                  { value: "WAC", label: "WAC (Weighted Average Cost)" },
                ]}
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
              <InputField
                label="Restock Level:"
                name="RestockLevel"
                type="number"
                min="0"
                value={formData.RestockLevel}
                onChange={handleChange}
                placeholder="10"
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
              <InputField
                label="Estimated Rate (PKR):"
                name="EstimatedRate"
                type="number"
                min="0"
                step="any"
                value={formData.EstimatedRate}
                onChange={handleChange}
                placeholder="265,000"
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
              <InputField
                label="Allowed Wastage (%):"
                name="AllowedWastage"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.AllowedWastage}
                onChange={handleChange}
                placeholder="3.5"
                stacked
                wrapperCol="col-lg-6 col-md-6 col-12"
              />
            </div>
          </div>
        </div>

        {/* Segment 3: Alternate Purchase Units (Bulk Conversions) */}
        <div className="add-stock-card" style={{ marginBottom: "20px" }}>
          <div className="bulk-header-actions">
            <div>
              <h5 className="add-stock-card-title" style={{ margin: 0 }}>
                Alternate Purchase Units (Bulk Conversions)
              </h5>
            </div>
            <button type="button" className="bulk-add-btn" onClick={handleAddPackaging}>
              <FiPlus size={15} /> Add Purchase Unit
            </button>
          </div>

          {packagings.length === 0 ? (
            <div className="bulk-empty-state">
              No alternate purchase units added yet. Click <strong>+ Add Purchase Unit</strong> if
              this material is procured in bulk packages (such as Bundles, Bags, Boxes, or Trucks)
              that convert to the base unit.
            </div>
          ) : (
            packagings.map((pkg, index) => {
              const deliveryNote = getDeliveryNote(pkg.packagingName, pkg.conversionToBase);

              return (
                <div key={pkg.id || index} className="packaging-unit-card">
                  <div className="packaging-row">
                    <div className="packaging-name-block">
                      <label>Packaging Name</label>
                      <input
                        type="text"
                        value={pkg.packagingName}
                        placeholder="e.g. Bundle, Bag, Box"
                        className="add-stock-input"
                        onChange={(e) =>
                          handlePackagingChange(index, "packagingName", e.target.value)
                        }
                      />
                    </div>

                    <div className="packaging-formula">
                      <span>1 [{pkg.packagingName.trim() || "Packaging"}] = [</span>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={pkg.conversionToBase}
                        placeholder="0.05"
                        className="add-stock-input packaging-conversion-input"
                        onChange={(e) =>
                          handlePackagingChange(index, "conversionToBase", e.target.value)
                        }
                      />
                      <span>]</span>
                      <div className="packaging-badge-block">{baseUomLabel} (Base Unit)</div>
                    </div>

                    <button
                      type="button"
                      className="packaging-delete-btn"
                      onClick={() => handleRemovePackaging(index)}
                      title="Delete Packaging Unit"
                    >
                      <FiTrash2 size={15} /> Delete
                    </button>
                  </div>

                  {deliveryNote && <div className="packaging-summary-note">{deliveryNote}</div>}
                </div>
              );
            })
          )}
        </div>

        {/* Footer Buttons */}
        <div className="float-right mt-4 mr-md-3 mb-5">
          <ActionButton
            text="Cancel"
            variant="cancel"
            onClick={() => history.push("/store/material")}
          />
          <ActionButton
            text={isEdit ? "Update" : "Save"}
            icon={BsCloudUpload}
            type="submit"
            isLoading={isLoading}
          />
        </div>
      </form>
    </div>
  );
};

export { AddStock };
export default AddStock;
